/**
 * Custom Klaviyo Back in Stock on the product page.
 * FR: Shopify Markets (shaktimat.fr). EN/ES/PT/DE: Weglot on the .de storefront.
 * Property written on the profile: "language_preference"
 */
const KLAVIYO_BIS_LANGUAGE_VALUES = {
  de: 'Deutsch',
  en: 'English',
  fr: 'Francais',
  es: 'Espanol',
  pt: 'Portuguese',
};

const KLAVIYO_BIS_LANGUAGE_CODES = Object.keys(KLAVIYO_BIS_LANGUAGE_VALUES);

class KlaviyoBis extends HTMLElement {
  #controller = null;
  #copy = {};
  #nativeObserver = null;
  #onVariantChanged = this.#handleVariantChange.bind(this);
  #onLanguageChanged = this.#applyLanguage.bind(this);
  #onSubmit = this.#handleSubmit.bind(this);

  connectedCallback() {
    this.form = this.querySelector('.klaviyo-bis__form');
    this.emailInput = this.querySelector('input[type="email"]');
    this.submitButton = this.querySelector('[data-bis-submit]');
    this.statusEl = this.querySelector('[data-bis-status]');
    this.newsletterWrap = this.querySelector('[data-bis-newsletter]');
    this.newsletterInput = this.querySelector('input[name="newsletter"]');

    this.#copy = this.#readJson('[data-klaviyo-bis-copy]') || {};
    this.#applyLanguage();
    this.#syncVisibility(this.dataset.available === 'true');
    this.#hideNativeTrigger();
    this.#observeNativeTrigger();

    this.form?.addEventListener('submit', this.#onSubmit);
    this.#productForm?.addEventListener('variant:change', this.#onVariantChanged);

    if (typeof Weglot !== 'undefined' && typeof Weglot.on === 'function') {
      Weglot.on('languageChanged', this.#onLanguageChanged);
    }
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#nativeObserver?.disconnect();
    this.form?.removeEventListener('submit', this.#onSubmit);
    this.#productForm?.removeEventListener('variant:change', this.#onVariantChanged);

    if (typeof Weglot !== 'undefined' && typeof Weglot.off === 'function') {
      Weglot.off('languageChanged', this.#onLanguageChanged);
    }
  }

  get #productForm() {
    const formId = this.dataset.formId;
    return formId ? document.forms[formId] : null;
  }

  get #companyId() {
    return this.dataset.companyId?.trim() || '';
  }

  get #revision() {
    return this.dataset.revision?.trim() || '2025-07-15';
  }

  get #debug() {
    return this.dataset.debug === 'true';
  }

  #log(...args) {
    if (this.#debug) {
      console.info('[klaviyo-bis]', ...args);
    }
  }

  #readJson(selector) {
    const node = this.querySelector(selector);
    if (!node) return null;

    try {
      return JSON.parse(node.textContent);
    } catch (error) {
      this.#log('Failed to parse JSON', selector, error);
      return null;
    }
  }

  #normalizeLang(raw) {
    const value = String(raw || '')
      .trim()
      .toLowerCase()
      .replace('_', '-');

    if (value.startsWith('pt')) return 'pt';
    if (KLAVIYO_BIS_LANGUAGE_CODES.includes(value)) return value;

    const short = value.split('-')[0];
    if (KLAVIYO_BIS_LANGUAGE_CODES.includes(short)) return short;

    return 'de';
  }

  #detectLang() {
    if (this.dataset.franceMarket === 'true') {
      return 'fr';
    }

    try {
      if (typeof Weglot !== 'undefined' && typeof Weglot.getCurrentLang === 'function') {
        const weglotLang = Weglot.getCurrentLang();
        if (weglotLang) return this.#normalizeLang(weglotLang);
      }
    } catch (error) {
      this.#log('Weglot language read failed', error);
    }

    const localeParam = new URL(window.location.href).searchParams.get('locale');
    if (localeParam) return this.#normalizeLang(localeParam);

    return this.#normalizeLang(document.documentElement.lang || 'de');
  }

  #strings() {
    const lang = this.#detectLang();
    return this.#copy[lang] || this.#copy.de || {};
  }

  #listIdFor(lang) {
    return (this.dataset[`list${lang.charAt(0).toUpperCase()}${lang.slice(1)}`] || '').trim();
  }

  #applyLanguage() {
    const lang = this.#detectLang();
    const strings = this.#strings();
    const listId = this.#listIdFor(lang);

    this.dataset.detectedLang = lang;
    this.#setText('[data-bis-heading]', strings.heading);
    this.#setText('[data-bis-email-label]', strings.email_label);
    this.#setText('[data-bis-submit]', strings.submit);
    this.#setText('[data-bis-newsletter-label]', strings.newsletter);

    if (this.emailInput && strings.email_label) {
      this.emailInput.setAttribute('aria-label', strings.email_label);
    }

    if (this.newsletterWrap) {
      this.newsletterWrap.hidden = !listId;
    }

    if (this.dataset.confirmed === 'true' && strings.success) {
      this.#setStatus(strings.success, 'success');
    }

    this.#log('language', lang, KLAVIYO_BIS_LANGUAGE_VALUES[lang], { listId: listId || null });
  }

  #setText(selector, value) {
    const node = this.querySelector(selector);
    if (node && value) node.textContent = value;
  }

  #syncVisibility(available) {
    this.hidden = Boolean(available);
  }

  #hideNativeTrigger() {
    const nodes = document.querySelectorAll('#klaviyo-bis-button-container, [data-a11y-identifier^="bis-button"]');
    for (const node of nodes) {
      node.hidden = true;
      node.setAttribute('aria-hidden', 'true');
      node.style.display = 'none';
    }
  }

  #observeNativeTrigger() {
    const root = this.#productForm || document.body;
    this.#nativeObserver = new MutationObserver(() => this.#hideNativeTrigger());
    this.#nativeObserver.observe(root, { childList: true, subtree: true });
  }

  #handleVariantChange(event) {
    this.#restoreForm();

    const variant = event.detail?.variant;
    if (!variant) {
      this.dataset.variantId = '';
      this.#syncVisibility(true);
      return;
    }

    this.dataset.variantId = String(variant.id);
    this.#syncVisibility(Boolean(variant.available));
  }

  #setStatus(message, type) {
    if (!this.statusEl) return;

    this.statusEl.textContent = '';
    this.statusEl.hidden = true;
    this.statusEl.classList.remove('klaviyo-bis__status--success', 'klaviyo-bis__status--error');

    requestAnimationFrame(() => {
      if (!this.statusEl) return;

      this.statusEl.textContent = message || '';
      this.statusEl.hidden = !message;
      if (type) this.statusEl.classList.add(`klaviyo-bis__status--${type}`);
    });

    if (this.emailInput) {
      this.emailInput.setAttribute('aria-invalid', type === 'error' ? 'true' : 'false');
    }
  }

  #confirm(message) {
    this.dataset.confirmed = 'true';
    this.#setStatus(message, 'success');

    requestAnimationFrame(() => {
      if (this.dataset.confirmed !== 'true') return;
      if (this.form) this.form.hidden = true;
      this.statusEl?.focus({ preventScroll: true });
    });
  }

  #restoreForm() {
    if (this.dataset.confirmed !== 'true' && !this.statusEl?.textContent) return;

    delete this.dataset.confirmed;
    if (this.form) this.form.hidden = false;
    if (this.submitButton) this.submitButton.disabled = false;
    this.#setStatus('', null);
  }

  #validEmail(email) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  }

  async #handleSubmit(event) {
    event.preventDefault();

    const strings = this.#strings();
    const email = this.emailInput?.value.trim() || '';
    const variantId = this.dataset.variantId;

    if (!this.#validEmail(email)) {
      this.#setStatus(strings.invalid_email, 'error');
      this.emailInput?.focus();
      return;
    }

    if (!variantId || !this.#companyId) {
      this.#setStatus(strings.error, 'error');
      return;
    }

    this.submitButton.disabled = true;
    this.#setStatus('', null);

    this.#controller?.abort();
    this.#controller = new AbortController();

    try {
      const lang = this.#detectLang();
      const languagePreference = KLAVIYO_BIS_LANGUAGE_VALUES[lang] || KLAVIYO_BIS_LANGUAGE_VALUES.de;
      const subscribeToNewsletter = Boolean(this.newsletterInput?.checked && this.#listIdFor(lang));

      await this.#createBackInStock(email, variantId);
      await this.#updateLanguagePreference(email, languagePreference);

      if (subscribeToNewsletter) {
        await this.#subscribeToList(email, this.#listIdFor(lang));
      }

      this.form?.reset();
      this.#confirm(strings.success);
      this.#log('signup ok', { email, variantId, languagePreference });
    } catch (error) {
      this.#log('signup failed', error);
      this.#setStatus(strings.error, 'error');
    } finally {
      if (this.dataset.confirmed !== 'true' && this.submitButton) {
        this.submitButton.disabled = false;
      }
    }
  }

  async #request(path, body) {
    const url = new URL(`https://a.klaviyo.com/client/${path}`);
    url.searchParams.set('company_id', this.#companyId);

    const response = await fetch(url.toString(), {
      method: 'POST',
      headers: {
        accept: 'application/json',
        'content-type': 'application/json',
        revision: this.#revision,
      },
      body: JSON.stringify(body),
      signal: this.#controller.signal,
    });

    if (!response.ok && response.status !== 202) {
      const details = await response.text().catch(() => '');
      throw new Error(`Klaviyo ${path} failed (${response.status}) ${details}`);
    }

    return response;
  }

  async #createBackInStock(email, variantId) {
    await this.#request('back-in-stock-subscriptions/', {
      data: {
        type: 'back-in-stock-subscription',
        attributes: {
          channels: ['EMAIL'],
          profile: {
            data: {
              type: 'profile',
              attributes: { email },
            },
          },
        },
        relationships: {
          variant: {
            data: {
              type: 'catalog-variant',
              id: `$shopify:::$default:::${variantId}`,
            },
          },
        },
      },
    });
  }

  async #updateLanguagePreference(email, languagePreference) {
    await this.#request('profiles/', {
      data: {
        type: 'profile',
        attributes: {
          email,
          properties: {
            language_preference: languagePreference,
          },
        },
      },
    });
  }

  async #subscribeToList(email, listId) {
    await this.#request('subscriptions/', {
      data: {
        type: 'subscription',
        attributes: {
          custom_source: 'Back in Stock',
          profile: {
            data: {
              type: 'profile',
              attributes: { email },
            },
          },
        },
        relationships: {
          list: {
            data: {
              type: 'list',
              id: listId,
            },
          },
        },
      },
    });
  }
}

if (!window.customElements.get('klaviyo-bis')) {
  window.customElements.define('klaviyo-bis', KlaviyoBis);
}
